import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface DateRangeParams {
  dateRange: [Date, Date];
  setDateRange: (dates: [Date, Date]) => void;
  startDateString: string;
  endDateString: string;
}

/**
 * Helper to format a Date as YYYY-MM-DD string
 */
const formatDateString = (date: Date): string => {
  const isoString = date.toISOString().split('T')[0];
  if (!isoString) {
    throw new Error('Failed to format date string');
  }
  return isoString;
};

/**
 * Hook to manage date range via URL search params
 *
 * Default: Last 30 days (inclusive of today)
 * URL format: ?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
 */
export function useDateRangeParams(): DateRangeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const dateRange = useMemo((): [Date, Date] => {
    const startStr = searchParams.get('start_date');
    const endStr = searchParams.get('end_date');

    if (startStr && endStr) {
      const startDate = new Date(startStr);
      const endDate = new Date(endStr);

      // Validate parsed dates - fall back to defaults if invalid
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        return [startDate, endDate];
      }
    }

    // Default: last 30 days (29 days ago + today = 30 days inclusive)
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);
    return [start, end];
  }, [searchParams]);

  const setDateRange = (dates: [Date, Date]): void => {
    const [start, end] = dates;
    setSearchParams(
      (prev): URLSearchParams => {
        prev.set('start_date', formatDateString(start));
        prev.set('end_date', formatDateString(end));
        return prev;
      },
      { replace: true }
    );
  };

  const startDateString = formatDateString(dateRange[0]);
  const endDateString = formatDateString(dateRange[1]);

  return {
    dateRange,
    setDateRange,
    startDateString,
    endDateString,
  };
}
